import { DEFAULT_STYLES, DEFAULT_STYLE } from "@/utils/bigScreenSchema";

/**
 * ss(theme, screen, element, legacyFallback)  ("screen style")
 *
 * Resolve a per-element style scoped to a specific screen. The final
 * object is merged in this order (later wins):
 *   1. Generic DEFAULT_STYLE (white / 1 / 0 / 0 / shadow=true)
 *   2. Schema default for (screen, element)
 *   3. Legacy fallback the caller may pass (e.g. an old flat theme field)
 *   4. User-configured display_styles[screen][element] from Mongo
 *
 * Returns: { color, size, offset_x, offset_y, shadow, bg_color?, border_color?, glow? }
 *          + a computed `textShadow` CSS string (respects the `shadow` flag).
 */
const DEFAULT_TEXT_SHADOW =
  "0 2px 4px rgba(0,0,0,0.5), 0 4px 12px rgba(0,0,0,0.65), 0 0 32px rgba(0,0,0,0.35)";

export function ss(theme, screen, element, legacyFallback = {}) {
  const schemaDefault = (DEFAULT_STYLES[screen] || {})[element] || {};
  const configured = ((theme?.display_styles || {})[screen] || {})[element] || {};
  const merged = { ...DEFAULT_STYLE, ...schemaDefault, ...legacyFallback, ...configured };
  // `shadow` defaults to TRUE — every text element gets the same
  // legibility drop-shadow as the huge timer (white-on-white protection).
  // Users can turn it off per-element via the Settings checkbox.
  if (merged.shadow === undefined) merged.shadow = true;
  merged.textShadow = merged.shadow === false ? "none" : DEFAULT_TEXT_SHADOW;
  return merged;
}

/** CSS transform helper (offset X/Y). */
export function offsetTransform(s) {
  const x = s?.offset_x || 0;
  const y = s?.offset_y || 0;
  if (!x && !y) return "";
  return `translate(${x}px, ${y}px)`;
}

/**
 * Mirrored variant. Used on side-by-side layouts (battle_run left/right,
 * battle_vote athlete1/athlete2, circle_run 4 corners) so a single
 * offset_x / offset_y value in Settings produces a strictly symmetric
 * layout: offset_x=+N means "N px away from center on both sides".
 */
export function offsetTransformMirrored(s, mirrorX = false, mirrorY = false) {
  const x = (s?.offset_x || 0) * (mirrorX ? -1 : 1);
  const y = (s?.offset_y || 0) * (mirrorY ? -1 : 1);
  if (!x && !y) return "";
  return `translate(${x}px, ${y}px)`;
}

/**
 * Standalone helper for callers that already have a raw style object
 * (e.g. from a legacy path) and just want the shadow CSS.
 */
export function textShadow(style) {
  if (!style || style.shadow === false) return "none";
  return DEFAULT_TEXT_SHADOW;
}

